import { inject, Injectable } from '@angular/core';
import { Resolve, ResolveFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { DatabaseService } from '../services/database.service';
import { LoggerService } from '../services/logger.service';
import { User, UserService } from '../services/user.service';

/**
 * UserResolver - Load current user data from database
 *
 * Purpose: Ensure user data is loaded before route activates
 *
 * Execution Order: Runs AFTER SyncGuard (data is synced)
 * Success: currentUser signal populated, user data available in route.data.user
 * Failure: Redirects to /error if user not found after sync
 *
 * Dependencies:
 * - AuthService: Get token and decode JWT
 * - UserService: Load user from database
 *
 * Part of authentication flow redesign (Phase 2)
 */
@Injectable({ providedIn: 'root' })
export class UserResolver implements Resolve<User | null> {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private router = inject(Router);
  private logger = inject(LoggerService).getLogger('user-resolver');
  private databaseService = inject(DatabaseService);

  async resolve(): Promise<User | null> {
    const resolverStart = performance.now();
    this.logger.info('🔄 START - Loading current user');
    this.logger.debug('UserResolver executing');

    // If the DB is paused (non-leader tab), the overlay is covering the UI.
    // Return null without redirecting — the overlay handles the UX.
    if (this.databaseService.isPaused) {
      this.logger.info('⏸ DB is paused (non-leader tab) — skipping user load');
      return null;
    }

    // Get token and extract user ID
    const token = this.authService.getToken();
    if (!token) {
      this.logger.warn('No authentication token found');
      await this.router.navigate(['/login']);
      return null;
    }

    const payload = this.authService.decodeJwt(token);
    if (!payload?.sub) {
      this.logger.error('Invalid token payload - missing sub claim');
      await this.router.navigate(['/login']);
      return null;
    }

    // At this point, sync has completed (SyncGuard ran before this)
    // Database should have user data - if not, it's a fatal error
    const user = await this.userService.loadCurrentUser(payload.sub);

    if (!user) {
      // Check if user has no organization yet (new signup flow)
      // If customer_id is absent from JWT, user hasn't created an org yet
      if (!payload.customer_id) {
        this.logger.info('User has no organization in JWT - redirecting to signup');
        await this.router.navigate(['/signup']);
        return null;
      }

      // User not found after sync - data integrity issue or deactivated user
      this.logger.error('❌ User not found in database after sync completion');
      // Note: duplicate log removed - logger.error already captures this
      // UserService already showed deactivation toast if user was deactivated
      // Redirect to error page for other cases
      await this.router.navigate(['/error']);
      return null;
    }

    const resolverTime = (performance.now() - resolverStart).toFixed(0);
    this.logger.info(`✅ User resolved successfully in ${resolverTime}ms: ${user.email}`);
    return user;
  }
}

/**
 * Functional resolver for use in route configuration
 * Delegates to UserResolver class
 */
export const userResolver: ResolveFn<User | null> = async () => {
  const resolver = inject(UserResolver);
  return resolver.resolve();
};
